import { I18nPluralPipe } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideMinus, lucidePlus } from '@ng-icons/lucide';
import { ChipComponent } from '@shared/components/chip/chip.component';
import { ExpansionPanel } from '@shared/components/expansion-panel/expansion-panel.component';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCheckboxImports } from '@spartan-ng/helm/checkbox';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmLabelImports } from '@spartan-ng/helm/label';
import { HlmRadioGroupImports } from '@spartan-ng/helm/radio-group';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { HlmSwitchImports } from '@spartan-ng/helm/switch';
import { distinctUntilChanged } from 'rxjs';

import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { CartographieStore } from '../../../../cartographie/store/cartographie.store';
import { ModalitéStore } from '../../../../modalités/store/modalité.store';
import { AlerteComponent } from '../../../../shared/components/alerte/alerte.component';
import { LabelRequiredDirective } from '../../../../shared/directives/label-required.directive';
import { NumberStepperInputComponent } from '../../../components/number-stepper-input/number-stepper-input.component';
import { FormControlNames } from '../../../models/form-control-names.model';
import { EtapeStore } from '../../../parcours-etape/store/etape.store';
import { DetailContratFormGroup } from '../detail-form-groups.types';
import { AnneeForm, EtapeDetailFormService } from '../étape-detail-form.service';
import { DetailCertificationComponent } from './detail-certification/detail-certification.component';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-detail-contrat',
  imports: [
    ReactiveFormsModule,
    HlmInputImports,
    HlmFieldImports,
    HlmRadioGroupImports,
    HlmSwitchImports,
    LabelRequiredDirective,
    HlmCheckboxImports,
    HlmSelectImports,
    BrnSelectImports,
    AlerteComponent,
    HlmInputGroupImports,
    ExpansionPanel,
    HlmIconImports,
    HlmLabelImports,
    ChipComponent,
    I18nPluralPipe,
    HlmButtonImports,
    NumberStepperInputComponent,
    HlmSeparatorImports,
    DetailCertificationComponent,
  ],
  templateUrl: './detail-contrat.component.html',
  providers: [provideIcons({ lucidePlus, lucideMinus })],
})
export class DetailContratComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly detailFormService = inject(EtapeDetailFormService);
  readonly #modaliteStore = inject(ModalitéStore);
  readonly #cartographieStore = inject(CartographieStore);
  readonly #etapeStore = inject(EtapeStore);
  readonly #destroyRef = inject(DestroyRef);

  readonly form = input.required<DetailContratFormGroup>();
  readonly modalites = computed(() => this.#modaliteStore.entities());
  readonly cartographie = computed(() => this.#cartographieStore.selectedEntity());
  readonly etape = computed(() => this.#etapeStore.selectedEntity());
  readonly referentiel = input.required<Référentiel>();

  readonly peutModifier = input.required<boolean>();
  readonly showErrors = input<boolean, boolean | undefined>(false, {
    transform: (input: boolean | undefined) => !!input,
  });

  protected readonly semaineMapping: { [k: string]: string } = {
    '=0': '# semaine',
    '=1': '# semaine',
    other: '# semaines',
  };

  protected readonly anMapping: { [k: string]: string } = {
    '=0': '# an',
    '=1': '# an',
    other: '# ans',
  };

  constructor() {
    effect(() => {
      const cartographie = this.cartographie();
      if (cartographie) {
        this.#modaliteStore.charger(cartographie.id);
      }
    });
  }

  ngOnInit(): void {
    this.duree.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef), distinctUntilChanged()).subscribe(() => {
      let nbrAnnées = this.duree.value;
      let length = this.repartitionsAnnee.value.length;
      if (nbrAnnées) {
        if (nbrAnnées < length) {
          for (let i = length - 1; i >= nbrAnnées; i--) {
            this.repartitionsAnnee.removeAt(i);
          }
        }

        for (let i = length; i < nbrAnnées; i++) {
          this.ajouterAnnee((i + 1) as 1 | 2 | 3);
        }
      }
    });
  }

  ajouterAnnee(indx: 1 | 2 | 3) {
    const anneeForm: FormGroup<AnneeForm> = this.#fb.nonNullable.group({
      nombreMois: this.#fb.control(0),
      nombreSemainesCFA: this.#fb.control(0),
      nombreSemainesEntreprise: this.#fb.control(0),
      numeroAnnee: indx,
    });
    this.repartitionsAnnee.push(anneeForm);
  }

  isChecked(typeContrat?: string) {
    let isChecked = false;
    if (this.typesContrat.value.length) {
      isChecked = typeContrat ? this.typesContrat.value.includes(typeContrat) : !!this.autreType.value;
    }
    return isChecked;
  }

  changed(checked: boolean, value: string) {
    if (!checked && value === 'autre') {
      this.autreType.setValue('');
      return;
    }

    if (checked && value !== 'autre') {
      this.typesContrat.setValue([...this.typesContrat.value, value]);
    } else if (this.typesContrat.value.includes(value)) {
      const typesContrat = this.typesContrat.value;
      const idxToDelete = typesContrat.findIndex((x) => x === value);
      typesContrat.splice(idxToDelete, 1);
      this.typesContrat.setValue(typesContrat);
    }
  }

  getAllMonths() {
    if (this.detailFormService.repartitionsAnnee.value.length === 0) return 0;

    return this.detailFormService.repartitionsAnnee.value
      .map((x) => x.nombreMois)
      .reduce((acc, element) => acc! + element!);
  }

  getAllWeeksCFA() {
    if (this.detailFormService.repartitionsAnnee.value.length === 0) return 0;

    return this.detailFormService.repartitionsAnnee.value
      .map((x) => x.nombreSemainesCFA)
      .reduce((acc, element) => acc! + (element ?? 0), 0);
  }

  getAllWeeksEntreprise() {
    if (this.detailFormService.repartitionsAnnee.value.length === 0) return 0;

    return this.detailFormService.repartitionsAnnee.value
      .map((x) => x.nombreSemainesEntreprise)
      .reduce((acc, element) => acc! + (element ?? 0), 0);
  }

  handleFermetureChange() {
    this.estOuverte.setValue(!this.estOuverte.value);
  }

  get typesContrat() {
    return this.detailFormService.typesContrat;
  }

  get autreType() {
    return this.detailFormService.autreType;
  }

  get type() {
    return this.detailFormService.type;
  }

  get estCertifiante() {
    return this.detailFormService.estCertifiante;
  }

  get certification() {
    return this.detailFormService.certification;
  }

  get estOuverte() {
    return this.detailFormService.estOuverte;
  }

  get duree() {
    return this.detailFormService.duree;
  }

  get repartitionsAnnee() {
    return this.form().get(FormControlNames.REPARTITIONS_ANNEE) as FormArray<FormGroup<AnneeForm>>;
  }

  getRepartitionInvalid(i: number, formControlName: string) {
    const control = this.repartitionsAnnee.controls.at(i)?.get(formControlName);

    return control?.invalid ?? false;
  }
}
